import React, { useEffect, useState, useCallback } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Typography, IconButton, Tooltip } from '@mui/material';
import { isEnabledModule } from '#component/project/form/helper';
import DisabledModuleMessage from '#component/project/form/project/project-modules/disabled-module-message';
import {
  AddButtonIcon,
  ButtonContainer,
  StyledButton,
  StyledPaper,
  StyledTitle
} from './styles';
import ProductItem from './product-item';
import { useIntersection } from '#hooks';
import { uuidv4 } from '#helper';

const intersectionOptions = {
  rootMargin: '-69px',
  threshold: [1]
};

/**
 * Renders form with submit, go back
 * and add product buttons.
 * @returns {React.FC}
 */

const ProductForm = props => {
  const {
    submitting,
    previousStep,
    nextStep,
    history,
    forceError,
    formModules,
    submitForm,
    saveDisabled
  } = props;
  const { sentinelRef, isIntersecting } = useIntersection(intersectionOptions);
  const { control, watch, handleSubmit, formState: { errors } } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
    keyName: 'fieldId'
  });
  const items = watch('products') ?? [];
  const arrayError = errors.products?.root?.message;
  const shipmentModuleEnabled = isEnabledModule('SHIPMENT', formModules);

  const [expanded, setExpanded] = useState(
    fields.length ? `products[${fields.length - 1}]` : false
  );

  const handleSetExpanded = useCallback(panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  }, []);

  useEffect(() => {
    if (fields.length) {
      setExpanded(`products[${fields.length - 1}]`);
    }
  }, [fields.length]);

  const handleCancelClick = () => {
    history.push('/project');
  };

  const addProductItem = handleSubmit(() => append({ id: uuidv4() }));

  return (
    <form onSubmit={submitForm} autoComplete="off">
      {shipmentModuleEnabled ? (
        <>
          <StyledTitle variant="h4" gutterBottom>
            Product settings
          </StyledTitle>
          <StyledPaper elevation={0}>
            {fields.map((field, index) => (
              <ProductItem
                key={field.fieldId}
                item={items[index] ?? field}
                index={index}
                member={`products[${index}]`}
                remove={remove}
                numberOfItems={fields.length}
                expanded={expanded === `products[${index}]`}
                handleSetExpanded={handleSetExpanded}
                forceError={forceError}
              />
            ))}
            {arrayError &&
              <Typography align="center" color="error">{arrayError}</Typography>}
          </StyledPaper>
        </>
      ) : <DisabledModuleMessage />}

      <div ref={sentinelRef} />
      <ButtonContainer
        isIntersecting={isIntersecting}
        shipmentModuleEnabled={shipmentModuleEnabled}
      >
        {shipmentModuleEnabled ?
          <div>
            <IconButton size="small" onClick={addProductItem} aria-label="Add product setting">
              <Tooltip title="Add product setting">
                <AddButtonIcon />
              </Tooltip>
            </IconButton>
          </div> : null}
        <div>
          <StyledButton variant="outlined" onClick={handleCancelClick}>
            Cancel
          </StyledButton>
          <StyledButton
            variant="outlined"
            onClick={previousStep}
          >
            Back
          </StyledButton>
          <StyledButton
            disabled={submitting}
            onClick={nextStep}
            variant="outlined"
          >
            Next
          </StyledButton>
          <StyledButton
            disabled={saveDisabled}
            type="submit"
            variant="contained"
            color="secondary"
          >
            Save
          </StyledButton>
        </div>
      </ButtonContainer>
    </form>
  );
};

export default ProductForm;
